import { Dialog, DialogContent, showToast, Button } from "@calcom/ui";
import AvailabilityPage from "@components/booking/pages/AvailabilityPage";
import BookingPage from "@components/booking/pages/BookingPage";
import type { AvailabilityPageProps } from "../../../pages/[user]/[type]";
import type { BookPageProps } from "../../../pages/[user]/book";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import BookingSuccess from "@components/booking/pages/BookingSuccess";
import { FiX } from "@calcom/ui/components/icon";
import BookingPageSkeleton from "@components/booking/BookingPageSkeleton";
import { BookingSuccessSkeleton } from "@components/booking/BookingSuccessSkeleton";
import { useIsEmbed } from "@calcom/embed-core/embed-iframe";
interface MeetingBookingModalProps {
  isOpen: boolean;
  slug: string;
  username: string;
  onClose: () => void;
}

type BookingStep = "availability" | "booking" | "success";

export function MeetingBookingModal({ isOpen, slug, username, onClose }: MeetingBookingModalProps) {
  const { t } = useLocale();
  const router = useRouter();
  const isEmbed = useIsEmbed();

  const [step, setStep] = useState<BookingStep>("availability");

  const [selectedSlot, setSelectedSlot] = useState<{
    date: string;
    type: number;
    slug: string;
  } | null>(null);
  const [uid, setUid] = useState<string | null>(null);

  const { data, isLoading } = trpc.viewer.meeting.getMeetingAvailibility.useQuery(
    { username, eventSlug: slug },
    {
      enabled: isOpen,
      onError: (error) => {
        showToast(error.message, "error");
        onClose();
      },
    }
  );

  const { data: bookingData, isLoading: bookingLoading } = trpc.viewer.meeting.getMeetingForBooking.useQuery(
    { username, eventSlug: slug },
    {
      enabled: isOpen && !!selectedSlot && step === "booking",
      onError: (error) => {
        showToast(error.message, "error");
        setStep("availability");
      },
    }
  );

  useEffect(() => {
    if (!isOpen) {
      setStep("availability");
      setSelectedSlot(null);
      setUid(null);
    }
  }, [isOpen]);

  if (!data || isLoading) return null;

  const handleSlotSelect = (slotData: { date: string; type: number; slug: string }) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          date: slotData.date,
          type: slotData.type.toString(),
          slug: slotData.slug,
          user: username,
        },
      },
      undefined,
      { shallow: true }
    );

    setSelectedSlot(slotData);
    setStep("booking");
  };

  const handleBookingConfirmation = (
    uid: string,
    email: string,
    eventTypeSlug: string,
    rescheduleUid: string,
    bookingStartTime: Date | undefined
  ) => {
    const { date, type, slug: querySlug, user, ...restQuery } = router.query;

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...restQuery,
          coach: router.query.coach,
          username: username,
          isSuccessBookingPage: true,
          email: email,
          eventTypeSlug: eventTypeSlug,
          ...(rescheduleUid &&
            bookingStartTime && {
              formerTime: bookingStartTime.toString(),
            }),
        },
      },
      undefined,
      { shallow: true }
    );
    setUid(uid);
    setStep("success");
  };

  const handleBookingCancel = () => {
    setSelectedSlot(null);
    setStep("availability");

    const { date, type, slug: querySlug, user, ...restQuery } = router.query;

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...restQuery,
          coach: router.query.coach,
          username: username,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleClose = () => {
    setSelectedSlot(null);
    setStep("availability");
    onClose();
  };

  const renderStep = () => {
    switch (step) {
      case "availability":
        return <AvailabilityPage {...(data as AvailabilityPageProps)} onSlotSelect={handleSlotSelect} />;

      case "booking":
        if (bookingLoading || !bookingData) {
          return <BookingPageSkeleton />;
        }
        return (
          <BookingPage
            {...(bookingData as BookPageProps)}
            handleBookingConfirmation={handleBookingConfirmation}
            handleCancel={handleBookingCancel}
          />
        );

      case "success":
        if (!uid) return <BookingSuccessSkeleton />;
        return <BookingSuccess uid={uid} />;

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={
          isEmbed
            ? "!h-[calc(100%_-_2rem)] !w-[calc(100%_-_2rem)] border border-gray-200 !p-0 sm:!w-[calc(100%_-_2rem)] sm:!max-w-full sm:!rounded-[0.875rem] [&>div>div]:!h-full [&>div>div]:!w-full [&>div]:!h-full [&>div]:!w-full [&_main]:!m-0 [&_main]:!p-0"
            : "h-fit !w-full  !min-w-0 !max-w-full !p-0 sm:!w-fit sm:!max-w-fit sm:!rounded [&_main]:!m-0 [&_main]:!p-0"
        }
        transparentBackdrop={isEmbed}
        enableOverflow
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}>
        <div className="absolute right-2 top-2 z-50">
          <Button
            type="button"
            color="minimal"
            variant="icon"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-md p-0">
            <FiX className="h-4 w-4" />
          </Button>
        </div>

        {renderStep()}
      </DialogContent>
    </Dialog>
  );
}
